import { updatePaginationCurrentPage } from 'actions/pagination'
import { getUsers } from 'actions/users'
import useDispatch from 'hooks/useDispatch'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPagination } from 'selectors/pagination'
import { selectUsers } from 'selectors/user'
import { history } from 'store'

// Kept in the URL (rather than component state alone) so a search survives
// unmounting Landing when navigating to a profile and back.
export const SEARCH_QUERY_PARAM = 'q'
// Set by Row before navigating to a profile, so the search page can
// highlight that row again on return.
export const ACTIVE_USER_PARAM = 'active'

const readUrlParam = (name: string): string | null =>
  new URLSearchParams(history.location.search).get(name)

// Computes the current URL's query string with the given params set (or
// cleared, for a `null` value) — without touching history.
export const buildUrlSearch = (updates: Record<string, string | null>) => {
  const params = new URLSearchParams(history.location.search)
  Object.entries(updates).forEach(([key, value]) =>
    value ? params.set(key, value) : params.delete(key)
  )
  return params.toString()
}

// Spreads `history.location` — history v4's bare-descriptor `replace` only
// inherits `pathname` and silently drops the rest of the query string/state.
export const replaceUrlSearch = (search: string) =>
  history.replace({ ...history.location, search })

export const useUsersTable = () => {
  const dispatch = useDispatch()
  const [isFetchingUsers, setIsFetchingUsers] = useState(false)
  const [searchQuery, setSearchQueryState] = useState<string | null>(() =>
    readUrlParam(SEARCH_QUERY_PARAM)
  )
  const [activeUserUid, setActiveUserUid] = useState<string | null>(() =>
    readUrlParam(ACTIVE_USER_PARAM)
  )

  const setSearchQuery = useCallback((query: string | null) => {
    setSearchQueryState(query)
    // A new search invalidates whatever row was last highlighted — leaving
    // it would highlight an unrelated row if that uid coincidentally
    // reappeared in the new results.
    setActiveUserUid(null)
    replaceUrlSearch(
      buildUrlSearch({
        [SEARCH_QUERY_PARAM]: query,
        [ACTIVE_USER_PARAM]: null,
      })
    )
  }, [])

  const paginationInfo = useSelector(ormState =>
    selectPagination(ormState, 'user', 'all')
  )

  const currentPage = paginationInfo?.current_page ?? 1
  const numPages = paginationInfo?.total_pages ?? 1
  const userIds = paginationInfo?.record_ids?.[currentPage] ?? []
  const [totalMembershipCount, setTotalMembershipCount] = useState(0)
  const users = (useSelector(state => selectUsers(state)) ?? []).filter(user =>
    userIds.includes(user.id)
  )

  const fetchUsers = useCallback(
    async (page: number, query?: string | null) => {
      setIsFetchingUsers(true)
      const params: Parameters<typeof getUsers>[0] = {
        page,
      }
      if (query && query.length > 0) {
        params.query = query
      }
      const response = await dispatch(getUsers(params))
      if (!response.error) {
        setTotalMembershipCount(response.payload.data.total_membership_count)
      }
      setIsFetchingUsers(false)
    },
    [dispatch]
  )

  const onChangePage = useCallback(
    async (page: number) => {
      if (page in (paginationInfo?.record_ids ?? {})) {
        // users for that page are already in redux,
        // update the current_page to show it
        window.scrollTo(0, 0)
        dispatch(updatePaginationCurrentPage({ type: 'user', id: 'all', page }))
        return
      }

      // fetch the new page
      window.scrollTo(0, 0)
      fetchUsers(page, searchQuery)
    },
    [dispatch, paginationInfo, fetchUsers, searchQuery]
  )

  useEffect(() => {
    fetchUsers(currentPage, searchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUsers, searchQuery])

  return {
    isFetchingUsers,
    onChangePage,
    currentPage,
    numPages,
    totalMembershipCount,
    users,
    searchQuery,
    setSearchQuery,
    activeUserUid,
  }
}
